/**
 * JWT 토큰 관련 유틸리티
 */

interface JWTPayload {
  sub: string; // user ID
  email: string;
  name: string;
  organizationType?: string;
  department?: string;
  roles: string[];
  permissions?: Array<{
    name: string;
    displayName: string;
  }>; // 권한은 JWT에서 제거되어 optional
  iat: number; // issued at
  exp: number; // expires
}

/**
 * JWT 토큰을 디코딩하여 페이로드 반환 (한글 지원)
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT는 header.payload.signature 형태
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[decodeJWT] 잘못된 JWT 형식');
      return null;
    }

    // Base64 URL 디코딩 (한글 지원)
    const payload = parts[1];

    // Base64 URL을 일반 Base64로 변환
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // 패딩 추가 (필요한 경우)
    while (base64.length % 4) {
      base64 += '=';
    }

    // UTF-8 디코딩을 위한 올바른 방법
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // UTF-8 텍스트로 디코딩
    const decoder = new TextDecoder('utf-8');
    const jsonString = decoder.decode(bytes);

    const parsed = JSON.parse(jsonString);

    console.log('[decodeJWT] JWT 디코딩 성공:', {
      sub: parsed.sub,
      email: parsed.email,
      name: parsed.name,
      roles: parsed.roles,
      permissionsCount: parsed.permissions?.length || 0,
      exp: new Date(parsed.exp * 1000).toISOString(),
    });

    return parsed;
  } catch (error) {
    console.error('[decodeJWT] JWT 디코딩 실패:', error);
    console.error('토큰:', token.substring(0, 50) + '...');
    return null;
  }
};

/**
 * JWT 토큰이 만료되었는지 확인
 */
export const isJWTExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload) return true;

  const now = Math.floor(Date.now() / 1000);
  const expired = payload.exp < now;

  if (expired) {
    console.warn('[isJWTExpired] JWT 토큰이 만료되었습니다');
  }

  return expired;
};

/**
 * JWT에서 사용자 정보 추출 (권한은 별도 API로 로드)
 */
export const extractUserFromJWT = (token: string) => {
  const payload = decodeJWT(token);
  if (!payload) return null;

  return {
    _id: payload.sub,
    email: payload.email,
    name: payload.name,
    organizationType: payload.organizationType || 'puntacompany', // JWT에서 가져오거나 기본값
    department: payload.department || 'development', // JWT에서 가져오거나 기본값
    roles: payload.roles.map((role) => ({ name: role })),
    permissions: [], // JWT에는 권한이 없으므로 빈 배열로 초기화, 별도 API로 로드
  };
};

/**
 * JWT 토큰 유효성 검사
 */
export const isValidJWT = (token: string): boolean => {
  if (!token) return false;

  // 기본 형태 검사
  if (token.split('.').length !== 3) return false;

  // 만료 검사
  if (isJWTExpired(token)) return false;

  return true;
};

/**
 * 개발 환경에서 JWT 정보 디버깅
 */
export const debugJWT = (token?: string): void => {
  const targetToken =
    token || localStorage.getItem('token') || getCookieValue('token');

  if (!targetToken) {
    console.log('[debugJWT] JWT 토큰이 없습니다');
    return;
  }

  console.log('[debugJWT] JWT 디버깅:', {
    토큰길이: targetToken.length,
    토큰앞부분: targetToken.substring(0, 50) + '...',
    유효성: isValidJWT(targetToken),
    만료여부: isJWTExpired(targetToken),
    페이로드: decodeJWT(targetToken),
  });
};

// 쿠키에서 값 가져오는 헬퍼 함수
function getCookieValue(name: string): string | null {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
}

/**
 * 이전 권한 정보를 localStorage에 저장하여 변경사항 감지
 */
const PERMISSION_CACHE_KEY = 'lastPermissionCount';
const PERMISSION_CHECK_SESSION_KEY = 'permissionCheckSession';

/**
 * JWT 해독 시 권한 변경 감지 및 알림 표시 (중복 방지)
 * 현재는 JWT에 권한이 없으므로 비활성화
 */
export const checkPermissionChangeOnDecode = (token: string): boolean => {
  // JWT에서 권한이 제거되었으므로 권한 변경 감지는 별도 API를 통해 처리
  console.log(
    '[checkPermissionChangeOnDecode] JWT 기반 권한 변경 감지는 비활성화됨 (권한은 별도 API로 관리)'
  );
  return false;
};

/**
 * 로그아웃 시 권한 캐시 초기화
 */
export const clearPermissionCache = (): void => {
  localStorage.removeItem(PERMISSION_CACHE_KEY);

  // 권한 알림 관련 모든 세션 스토리지 정리
  const keys = Object.keys(sessionStorage);
  keys.forEach((key) => {
    if (
      key.startsWith('permission_alert_shown_') ||
      key === PERMISSION_CHECK_SESSION_KEY
    ) {
      sessionStorage.removeItem(key);
    }
  });

  console.log(
    '[clearPermissionCache] 권한 캐시 완전 초기화 (localStorage + sessionStorage)'
  );
};

// 개발 환경에서 전역 함수로 노출
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).debugJWT = debugJWT;
  (window as any).clearPermissionCache = clearPermissionCache;
}
